import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ConfirmDialog from "../../pages/common/ConfirmDialog";
import {
  Pagination,
  Empty,
  AutoComplete,
  Input,
  InputNumber,
  Select,
  Space,
  DatePicker,
} from "antd";
import BreadcrumbItems from "../../components/BreadcrumbItems";
import HandleDevices from "../common/HandleDevices";
import exportToExcel from "../../utils/exportToExcel";
import { signalTypeByRssi } from "../../utils/signalType";
import { SET_SELECTED_DEVICE } from "../../redux/actions/dashboardActions";
import { signalTextToRssi } from "./../../utils/signalType";
import GenerateColumns from "../common/GenerateColumns";
import Td from "../common/Td";
import { formatDate } from "../../utils/formatDate";
import axiosLLInstance from "../../utils/axiosLLInstance";
import { notifyUser } from "../../utils/notifyUser";
import { calendar } from "../Dashboard/icons";
import { search } from "../Dashboard/icons";
import TwoFactAuth from "./../../components/TwoFactAuth/TwoFactAuth";
import { HiOutlineArrowUpTray } from "react-icons/hi2";
import {
  Action,
  PageSizeOptions,
  PermissionActions,
  Role,
} from "../../utils/constants";
import { isAccessible } from "../../utils/isAccessible";

const columns = [
  "Model",
  "IMEI",
  "Status",
  "Network Strength",
  "Network Type",
  "SIM Operator",
  "Last Seen",
];

export default function FleetDevices() {
  const [imeiFile, setImeiFile] = useState({
    action: "",
    formData: {},
    url: "",
  });
  const [imeiOpt, setImeiOpt] = useState(false);
  const { fleetId } = useParams();
  const [options, setOptions] = useState([]);
  const [lastQuery, setLastQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const hasRendered = useRef(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState({
    status: "",
    date: "",
    state: "",
  });
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(PageSizeOptions.pageSize);
  const [filteredDevices, setFilteredDevices] = useState({
    devices: [],
    total: 0,
  });
  const { role, permissions = {} } = useSelector((state) => state.auth);
  const { fleet, devices, total } = useSelector((state) => state.fleets);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const searchDevice = async (queryValue) => {
    try {
      const { data } = await axiosLLInstance.get(
        "/devices/search_within_fleet",
        {
          params: {
            id: fleetId,
            imei: queryValue,
          },
        },
      );
      if (!data.length) {
        notifyUser({
          message: "No Match Found",
          type: "warning",
        });
        setOptions([]);
        return;
      }
      const options = data.map((device) => ({
        label: device.imei,
        value: JSON.stringify(device),
      }));
      setOptions(options);
    } catch (error) {
      console.error("Error fetching devices:", error);
    }
  };

  const onSelect = (device) => {
    const selectedDevice = JSON.parse(device);
    dispatch(SET_SELECTED_DEVICE(selectedDevice));
    const path =
      role === Role.SUPERADMIN
        ? `/dashboard/devices-analytics`
        : `/devices-analytics`;

    navigate(`${path}/${selectedDevice.imei}`);
  };

  const fetchDevices = async (page = 1, limit = PageSizeOptions.pageSize) => {
    try {
      const filterSelected =
        selectedFilter.status || selectedFilter.date || selectedFilter.state;
      const url = filterSelected
        ? `/devices/filter/${fleetId}`
        : `/devices/fleets/${fleetId}`;
      const params = filterSelected
        ? {
            page,
            limit,
            date: selectedFilter.date,
            status: selectedFilter.status,
            state: selectedFilter.state,
          }
        : { page, limit };
      const { data } = await axiosLLInstance.get(url, {
        params,
      });
      const temp = {
        devices: data.devices.map((device) => ({
          ...device,
          rssi: signalTypeByRssi(device.rssi),
        })),
        total: data.total,
      };
      setFilteredDevices(temp);
    } catch (error) {
      console.error("Error fetching devices", error);
    }
  };

  useEffect(() => {
    fetchDevices(current, pageSize);
  }, [current, pageSize, devices, total]);

  useEffect(() => {
    if (hasRendered.current) {
      if (current !== 1) {
        setCurrent(1);
      } else {
        fetchDevices(1, pageSize);
      }
    }
    hasRendered.current = true;
  }, [selectedFilter]);

  const handlePaginationChange = (current, size) => {
    setCurrent(current);
    setPageSize(size);
  };

  const handleClick = (device) => {
    dispatch(SET_SELECTED_DEVICE(device));
    const path =
      role === Role.SUPERADMIN
        ? `/dashboard/devices-analytics`
        : `/devices-analytics`;

    navigate(`${path}/${device.imei}`);
  };

  const downloadSample = () => {
    exportToExcel({
      data: [{ imei: "xxxxxxxxxxxxxxx", device_id: "xxxxxxxx" }],
      filename: "devices_sample.xlsx",
    });
  };

  const downloadDispatchFile = () => {
    exportToExcel({
      data: [{ imei: "xxxxxxxxxxxxxxx", dispatch_date: "DD-MM-YYYY" }],
      filename: "dispatch_sample.xlsx",
    });
  };

  const handleSearch = async () => {
    if (lastQuery !== searchQuery.trim()) {
      setLastQuery(searchQuery.trim());
      searchDevice(searchQuery);
    }
  };

  const handleChange = (value) => {
    if (!value.trim()) {
      setOptions([]);
      setLastQuery("");
      setSearchQuery("");
    }
    setSearchQuery(value);
  };

  const handleStatusChange = (value) => {
    // setCurrent(1);
    setSelectedFilter((prev) => ({
      ...prev,
      status: value,
    }));
  };

  const handleDateChange = (_, date) => {
    // setCurrent(1);
    setSelectedFilter((prev) => ({
      ...prev,
      date: date,
    }));
  };

  const handleStateChange = async (value) => {
    console.log(value);
    setSelectedFilter((prev) => ({
      ...prev,
      state: value,
    }));
  };

  const handleExport = async () => {
    try {
      const { data, headers } = await axiosLLInstance.get(
        `/fleets/export/${fleetId}`,
        {
          responseType: "arraybuffer",
          params: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      );

      const blob = new Blob([data], {
        type:
          headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${fleet.thing_group}_devices.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      notifyUser({
        message: "Download Complete",
        type: "success",
      });
    } catch (error) {
      console.error("error exporting devices:", error);
    }
  };

  const handleDelete = async () => {
    try {
      const { data } = await axiosLLInstance.delete(`/fleets/${fleet.id}`);
      notifyUser({
        message: data?.message || "Fleet deleted",
        type: "success",
      });
      navigate("/fleets");
    } catch (error) {
      console.error(error, "Error deleting fleet");
    } finally {
      setShowConfirm(false);
    }
  };

  const verifyUser = () => {
    axiosLLInstance
      .post(`/email/otp`)
      .then(() => {
        document.getElementById("my_modal_2").showModal();
      })
      .catch((error) => {
        console.error("Error verifying", error);
      })
      .finally(setShowConfirm(false));
  };

  const handle2FA = async () => {
    if (imeiOpt) {
      try {
        const { url, formData } = imeiFile;

        const { data } = await axiosLLInstance.post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        notifyUser({
          message: data?.message || imeiFile.action,
          type: "success",
        });
      } catch (error) {
        console.error("Error upload file", error);
      } finally {
        setImeiFile({
          action: "",
          formData: {},
          url: "",
        });
        setImeiOpt(false);
      }
    } else {
      handleDelete();
    }
  };

  return (
    <>
      <TwoFactAuth handle2FA={handle2FA} />
      <div className="flex items-center justify-between">
        <BreadcrumbItems
          items={[
            { title: fleet.thing_group || "Fleets", to: `/fleets` },
            { title: "Devices" },
          ]}
        />
        <div className="search-adminBox flex items-center gap-2">
          <AutoComplete
            className="h-12 border-gray-200 hover:border-gray-200 focus:border-gray-200 focus-within:border-gray-200 focus-within:shadow-none"
            onChange={handleChange}
            options={options}
            onSelect={onSelect}
          >
            <Input
              size="large"
              className="h-12 border-gray-200 hover:border-gray-200 focus:border-gray-200 focus-within:border-gray-200 focus-within:shadow-none"
              placeholder="Search IMEI"
              prefix={
                <img src={search} alt="search icon" onClick={handleSearch} />
              }
            />
          </AutoComplete>
          {role !== Role.USER && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.ADD}
              fleetId={fleetId}
            />
          )}
          {role !== Role.USER && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.WHITELIST}
              fleetId={fleetId}
            />
          )}
          {role !== Role.USER && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.BLACKLIST}
              fleetId={fleetId}
            />
          )}
          {role === Role.SUPERADMIN && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.UPDATE_DISPATCH}
              fleetId={fleetId}
            />
          )}
          {role === Role.SUPERADMIN && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.UPDATE_CONFIG}
              fleetId={fleetId}
            />
          )}
          {isAccessible({
            role,
            permissions,
            name: PermissionActions.FLEETS,
            action: PermissionActions.EXPORT,
          }) && (
            <div className="adminBtn">
              <button
                onClick={handleExport}
                className="btn btn-neutral font-bold py-2 px-4 rounded-[10px] flex items-center justify-between text-[14px]"
              >
                Export <HiOutlineArrowUpTray className="pl-1 text-[25px]" />
              </button>
            </div>
          )}

          {role !== Role.USER && (
            <HandleDevices
              setImeiFile={setImeiFile}
              setImeiOpt={setImeiOpt}
              action={Action.REVOKE}
              fleetId={fleetId}
            />
          )}
          {role === Role.SUPERADMIN && (
            <button
              onClick={() => setShowConfirm(true)}
              className="btn bg-white text-black font-bold py-2 px-4 rounded-[10px] flex items-center justify-between text-[14px]  mr-2 border border-base-content/20"
            >
              {Action.DELETE}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-trash3"
                viewBox="0 0 16 16"
              >
                <path d="M6.5 1h3a.5.5 0 0 1 .5.5v1H6v-1a.5.5 0 0 1 .5-.5M11 2.5v-1A1.5 1.5 0 0 0 9.5 0h-3A1.5 1.5 0 0 0 5 1.5v1H1.5a.5.5 0 0 0 0 1h.538l.853 10.66A2 2 0 0 0 4.885 16h6.23a2 2 0 0 0 1.994-1.84l.853-10.66h.538a.5.5 0 0 0 0-1zm1.958 1-.846 10.58a1 1 0 0 1-.997.92h-6.23a1 1 0 0 1-.997-.92L3.042 3.5zm-7.487 1a.5.5 0 0 1 .528.47l.5 8.5a.5.5 0 0 1-.998.06L5 5.03a.5.5 0 0 1 .47-.53Zm5.058 0a.5.5 0 0 1 .47.53l-.5 8.5a.5.5 0 1 1-.998-.06l.5-8.5a.5.5 0 0 1 .528-.47M8 4.5a.5.5 0 0 1 .5.5v8.5a.5.5 0 0 1-1 0V5a.5.5 0 0 1 .5-.5" />
              </svg>
            </button>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        {role !== Role.USER && (
          <div className="flex justify-end sample_download my-2">
            <span className="cursor-pointer" onClick={downloadSample}>
              Download Sample file
            </span>
          </div>
        )}
        {role === Role.SUPERADMIN && (
          <div className="flex gap-2 items-center sample_download">
            <span>|</span>
            <span className="cursor-pointer" onClick={downloadDispatchFile}>
              Sample dispatch file
            </span>
          </div>
        )}
      </div>
      <div className="flex gap-2 justify-end items-center">
        <Select
          style={{ width: "7rem" }}
          size="large"
          allowClear
          placeholder="Status"
          options={[
            { label: "Online", value: "Online" },
            { label: "Offline", value: "Offline" },
          ]}
          onChange={handleStatusChange}
        />
        {/* <Select
          size="large"
          style={{ width: "8rem", outline: 0, border: 0 }}
          allowClear
          placeholder="State"
          options={[
            { label: "ACTIVE", value: "ACTIVE" },
            { label: "INACTIVE", value: "INACTIVE" },
          ]}
          onChange={handleStateChange}
        /> */}
        <div className="border px-4 py-2 rounded-[10px] bg-white border border-gray-200 datePicker">
          <Space direction="vertical">
            <DatePicker
              suffixIcon={<img src={calendar} alt="calendar" />}
              size="small"
              variant="borderless"
              onChange={handleDateChange}
            />
          </Space>
        </div>
      </div>
      <div className="mt-3">
        <div className="overflow-x-auto mt-6">
          <table className="table">
            <thead className="border-b-2 border-base-300">
              <GenerateColumns columns={columns} />
            </thead>
            <tbody className="mt-3">
              {filteredDevices.total > 0 ? (
                filteredDevices.devices.map(
                  ({
                    imei,
                    device_id,
                    fleet_id,
                    certificate_id,
                    mdl_id,
                    presence,
                    rssi,
                    net_t,
                    op,
                    updated_at,
                    thing_group,
                    status,
                  }) => (
                    <tr
                      onClick={() =>
                        handleClick({
                          imei,
                          certificate_id,
                          status,
                          mdl_id,
                          presence,
                          fleet_id,
                          device_id,
                          thing_group,
                        })
                      }
                      key={imei}
                      className="shadow-[0_3.5px_5.5px_0_#00000005] h-16 cursor-pointer"
                    >
                      <Td className={"rounded-l-[15px]"}>{mdl_id || "-"}</Td>
                      <Td>{imei}</Td>
                      <Td
                        className={
                          presence === "Online"
                            ? "text-green-500"
                            : "text-red-500"
                        }
                      >
                        {presence || "-"}
                      </Td>
                      <Td>
                        {presence === "Online" ? signalTypeByRssi(rssi) : "-"}
                      </Td>
                      <Td>{presence === "Online" ? net_t : "-"}</Td>
                      <Td> {presence === "Online" ? op : "-"}</Td>
                      <Td className={"rounded-r-[15px]"}>
                        {updated_at ? formatDate(updated_at) : "-"}
                      </Td>
                    </tr>
                  ),
                )
              ) : (
                <tr>
                  <td colSpan={columns.length}>
                    <Empty />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {filteredDevices.total > 0 && (
          <div className="flex justify-end mt-4 w-[100%]">
            <Pagination
              current={current}
              total={filteredDevices.total}
              pageSize={pageSize}
              onChange={handlePaginationChange}
              showSizeChanger={true}
              pageSizeOptions={PageSizeOptions.options}
            />
          </div>
        )}
      </div>
      {showConfirm && (
        <ConfirmDialog
          content={
            <p>
              Are you sure, you want to delete{" "}
              <strong>{fleet.thing_group} </strong> fleet
            </p>
          }
          onOk={verifyUser}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </>
  );
}
